/**
 * Pure document operations used by the editor. Every function returns a new
 * document and never mutates its input, so History can keep plain references.
 */
import {
  LIMITS,
  OBJECT_TYPES,
  defaultParams,
  isGeometryType,
  type CustomMaterial,
  type ObjectType,
  type ParamSpec,
  type ParamValue,
  type TrackDocument,
  type TrackObject,
} from '../authoring/TrackDocument';
import { IDENTITY, canon, compose, quatAxisAngle, quatFromEulerDeg, quatMul, quatToEulerDeg, rotateVec, type Quat, type RigidTransform } from '../authoring/transform';
import type { Vec3Tuple } from '../world/LevelData';

const conj = (q: Quat): Quat => ({ x: -q.x, y: -q.y, z: -q.z, w: q.w });
const sub = (a: Vec3Tuple, b: Vec3Tuple): Vec3Tuple => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
/** A copy of the object without its parent link. */
function detached(o: TrackObject): TrackObject {
  const copy = { ...o };
  delete copy.parent;
  return copy;
}
const c3 = (v: Vec3Tuple, step = 1e-6): Vec3Tuple => [canon(v[0], step), canon(v[1], step), canon(v[2], step)];

export function findObject(doc: TrackDocument, id: string): TrackObject | undefined {
  return doc.objects.find((o) => o.id === id);
}

/** A readable id unique in `doc`: "<base>-<n>". */
export function newObjectId(doc: TrackDocument, base: string, taken: ReadonlySet<string> = new Set()): string {
  const used = new Set(doc.objects.map((o) => o.id));
  const stem = base.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 40) || 'object';
  for (let n = 1; ; n++) {
    const id = `${stem}-${n}`;
    if (!used.has(id) && !taken.has(id)) return id;
  }
}

/** The object, its children, their children … in document order. */
export function subtreeIds(doc: TrackDocument, ids: Iterable<string>): Set<string> {
  const out = new Set(ids);
  let grew = true;
  while (grew) {
    grew = false;
    for (const o of doc.objects) {
      if (o.parent !== undefined && out.has(o.parent) && !out.has(o.id)) {
        out.add(o.id);
        grew = true;
      }
    }
  }
  return out;
}

/** World transform of an object through its group chain. */
export function worldTransformOf(doc: TrackDocument, id: string): RigidTransform {
  const chain: TrackObject[] = [];
  const seen = new Set<string>();
  for (let o = findObject(doc, id); o && !seen.has(o.id); o = o.parent !== undefined ? findObject(doc, o.parent) : undefined) {
    seen.add(o.id);
    chain.unshift(o);
  }
  return chain.reduce<RigidTransform>((acc, o) => compose(acc, { position: o.position, rotation: quatFromEulerDeg(o.rotationDeg) }), IDENTITY);
}

function parentWorld(doc: TrackDocument, o: TrackObject): RigidTransform {
  return o.parent !== undefined ? worldTransformOf(doc, o.parent) : IDENTITY;
}

function replaceObjects(doc: TrackDocument, f: (o: TrackObject) => TrackObject): TrackDocument {
  let changed = false;
  const objects = doc.objects.map((o) => {
    const n = f(o);
    if (n !== o) changed = true;
    return n;
  });
  return changed ? { ...doc, objects } : doc;
}

/** Clamps and coerces a parameter value to its spec. */
export function coerceParam(spec: ParamSpec, value: ParamValue): ParamValue {
  switch (spec.kind) {
    case 'number': {
      const n = Number(value);
      return Number.isFinite(n) ? Math.min(spec.max, Math.max(spec.min, n)) : spec.default;
    }
    case 'int': {
      const n = Math.round(Number(value));
      return Number.isFinite(n) ? Math.min(spec.max, Math.max(spec.min, n)) : spec.default;
    }
    case 'bool':
      return value === true;
    case 'enum':
      return typeof value === 'string' && spec.options.includes(value) ? value : spec.default;
    case 'text':
      return String(value).slice(0, spec.maxLength);
    case 'color': {
      const n = Math.round(Number(value));
      return Number.isFinite(n) ? Math.min(0xffffff, Math.max(0, n)) : spec.default;
    }
    case 'points': {
      if (!Array.isArray(value)) return spec.default;
      const pts = (value as readonly Vec3Tuple[]).filter((p) => Array.isArray(p) && p.length === 3 && p.every(Number.isFinite)).slice(0, spec.maxCount);
      return pts.length >= spec.minCount ? pts.map((p) => c3(p)) : spec.default;
    }
  }
}

export interface AddOptions {
  parent?: string;
  material?: string;
  rotationDeg?: Vec3Tuple;
  name?: string;
}

export function addObject(doc: TrackDocument, type: ObjectType, position: Vec3Tuple, opts: AddOptions = {}): { doc: TrackDocument; id: string } {
  const id = newObjectId(doc, type);
  const info = OBJECT_TYPES[type];
  let params = defaultParams(type);
  if (type === 'checkpoint') params = { ...params, index: doc.objects.filter((o) => o.type === 'checkpoint').length };
  const obj: TrackObject = {
    id,
    name: opts.name ?? `${info.label} ${id.slice(type.length + 1)}`,
    type,
    ...(opts.parent !== undefined ? { parent: opts.parent } : {}),
    position: c3(position),
    rotationDeg: opts.rotationDeg ?? [0, 0, 0],
    ...(isGeometryType(type) ? { material: opts.material ?? 'safe.stone' } : {}),
    params,
  };
  return { doc: { ...doc, objects: [...doc.objects, obj] }, id };
}

export type ObjectPatch = Partial<Pick<TrackObject, 'name' | 'position' | 'rotationDeg' | 'material' | 'hidden' | 'locked'>>;

export function updateObject(doc: TrackDocument, id: string, patch: ObjectPatch): TrackDocument {
  return replaceObjects(doc, (o) => {
    if (o.id !== id) return o;
    const next: TrackObject = { ...o, ...patch };
    if (patch.position) next.position = c3(patch.position);
    if (patch.rotationDeg) next.rotationDeg = c3(patch.rotationDeg);
    if (patch.hidden === false) delete next.hidden;
    if (patch.locked === false) delete next.locked;
    if (!isGeometryType(o.type)) delete next.material;
    return next;
  });
}

export function setParam(doc: TrackDocument, id: string, key: string, value: ParamValue): TrackDocument {
  return replaceObjects(doc, (o) => {
    if (o.id !== id) return o;
    const spec = (OBJECT_TYPES[o.type].params as Readonly<Record<string, ParamSpec>>)[key];
    if (!spec) return o;
    return { ...o, params: { ...o.params, [key]: coerceParam(spec, value) } };
  });
}

/** Sets an object's world transform (the gizmo works in world space). */
export function setWorldTransform(doc: TrackDocument, id: string, world: RigidTransform): TrackDocument {
  const o = findObject(doc, id);
  if (!o) return doc;
  const pw = parentWorld(doc, o);
  const inv = conj(pw.rotation);
  const position = rotateVec(inv, sub(world.position, pw.position));
  const rotation = quatMul(inv, world.rotation);
  return updateObject(doc, id, { position, rotationDeg: quatToEulerDeg(rotation) });
}

/** Moves objects by a world-space offset, whatever their parents' rotations. */
export function translateObjects(doc: TrackDocument, ids: Iterable<string>, delta: Vec3Tuple): TrackDocument {
  const roots = topLevel(doc, ids);
  let out = doc;
  for (const id of roots) {
    const o = findObject(out, id)!;
    const local = rotateVec(conj(parentWorld(out, o).rotation), delta);
    out = updateObject(out, id, { position: [o.position[0] + local[0], o.position[1] + local[1], o.position[2] + local[2]] });
  }
  return out;
}

/** The given ids minus any whose ancestor is also given (moving a group already moves its children). */
export function topLevel(doc: TrackDocument, ids: Iterable<string>): string[] {
  const set = new Set(ids);
  return [...set].filter((id) => {
    const seen = new Set<string>();
    for (let o = findObject(doc, id); o?.parent !== undefined && !seen.has(o.id); o = findObject(doc, o.parent)) {
      seen.add(o.id);
      if (set.has(o.parent)) return false;
    }
    return findObject(doc, id) !== undefined;
  });
}

/** Deletes objects and everything grouped under them. */
export function deleteObjects(doc: TrackDocument, ids: Iterable<string>): TrackDocument {
  const gone = subtreeIds(doc, ids);
  return gone.size ? { ...doc, objects: doc.objects.filter((o) => !gone.has(o.id)) } : doc;
}

/** Copies the selected subtrees with fresh ids, offset in world space. Returns the copies' root ids. */
export function duplicateObjects(doc: TrackDocument, ids: Iterable<string>, offset: Vec3Tuple = [1, 0, 1]): { doc: TrackDocument; ids: string[] } {
  const roots = topLevel(doc, ids);
  const all = subtreeIds(doc, roots);
  const remap = new Map<string, string>();
  const taken = new Set<string>();
  for (const o of doc.objects) {
    if (!all.has(o.id)) continue;
    const nid = newObjectId(doc, o.id.replace(/-\d+$/, ''), taken);
    taken.add(nid);
    remap.set(o.id, nid);
  }
  const copies = doc.objects
    .filter((o) => all.has(o.id))
    .map((o): TrackObject => {
      const parent = o.parent !== undefined ? (remap.get(o.parent) ?? o.parent) : undefined;
      return { ...detached(o), id: remap.get(o.id)!, name: roots.includes(o.id) ? `${o.name} copy` : o.name, ...(parent !== undefined ? { parent } : {}) };
    });
  let out: TrackDocument = { ...doc, objects: [...doc.objects, ...copies] };
  const newRoots = roots.map((id) => remap.get(id)!);
  out = translateObjects(out, newRoots, offset);
  return { doc: out, ids: newRoots };
}

/** Wraps objects that share a parent in a new group at their centroid. */
export function groupObjects(doc: TrackDocument, ids: Iterable<string>): { doc: TrackDocument; id: string } | null {
  const roots = topLevel(doc, ids);
  if (!roots.length) return null;
  const objs = roots.map((id) => findObject(doc, id)!);
  const parent = objs[0]!.parent;
  if (objs.some((o) => o.parent !== parent)) return null;
  const centre = objs.reduce<Vec3Tuple>((c, o) => [c[0] + o.position[0] / objs.length, c[1] + o.position[1] / objs.length, c[2] + o.position[2] / objs.length], [0, 0, 0]);
  const added = addObject(doc, 'group', centre, { ...(parent !== undefined ? { parent } : {}) });
  const gid = added.id;
  const set = new Set(roots);
  const objects = added.doc.objects.map((o) => (set.has(o.id) ? { ...o, parent: gid, position: c3(sub(o.position, centre)) } : o));
  // Keep the group where its first member was in the list so the hierarchy reads naturally.
  const group = objects.pop()!;
  const at = objects.findIndex((o) => set.has(o.id));
  objects.splice(at, 0, group);
  return { doc: { ...added.doc, objects }, id: gid };
}

/** Removes a group, keeping its children where they are in the world. */
export function ungroup(doc: TrackDocument, groupId: string): TrackDocument {
  const g = findObject(doc, groupId);
  if (!g || g.type !== 'group') return doc;
  const gLocal: RigidTransform = { position: g.position, rotation: quatFromEulerDeg(g.rotationDeg) };
  const objects = doc.objects
    .filter((o) => o.id !== groupId)
    .map((o) => {
      if (o.parent !== groupId) return o;
      const t = compose(gLocal, { position: o.position, rotation: quatFromEulerDeg(o.rotationDeg) });
      return { ...detached(o), ...(g.parent !== undefined ? { parent: g.parent } : {}), position: c3(t.position), rotationDeg: c3(quatToEulerDeg(t.rotation)) };
    });
  return { ...doc, objects };
}

const MIRROR_TURN_TYPES: ReadonlySet<ObjectType> = new Set(['curvedRamp', 'arc']);

/**
 * Reflection in x (across the plane x = centre of the selection). A reflection
 * isn't a rotation, so it is pushed down the subtree: every local transform is
 * conjugated (x ↦ −x, rotation (x, y, z, w) ↦ (x, −y, −z, w)) and every shape
 * is replaced by its mirror image (turn direction, path points, wall arcs).
 */
export function mirrorObjects(doc: TrackDocument, ids: Iterable<string>): TrackDocument {
  const roots = topLevel(doc, ids);
  if (!roots.length) return doc;
  const cx = roots.reduce((s, id) => s + findObject(doc, id)!.position[0], 0) / roots.length;
  const rootSet = new Set(roots);
  const all = subtreeIds(doc, roots);
  return replaceObjects(doc, (o) => {
    if (!all.has(o.id)) return o;
    const q = quatFromEulerDeg(o.rotationDeg);
    let rot: Quat = { x: q.x, y: -q.y, z: -q.z, w: q.w };
    let params = o.params;
    if (MIRROR_TURN_TYPES.has(o.type)) params = { ...params, angle: -Number(params['angle'] ?? (OBJECT_TYPES[o.type].params as Record<string, ParamSpec>)['angle']!.default) };
    if (o.type === 'path') params = { ...params, points: ((params['points'] as readonly Vec3Tuple[] | undefined) ?? (OBJECT_TYPES.path.params.points.default as readonly Vec3Tuple[])).map((p) => [-p[0], p[1], p[2]] as Vec3Tuple) };
    if (o.type === 'curvedWall') {
      // An arc over [0, A] mirrors to [180 − A, 180]: the same arc turned by 180 − A about local Y.
      const a = Number(params['angle'] ?? OBJECT_TYPES.curvedWall.params.angle.default);
      rot = quatMul(rot, quatAxisAngle([0, 1, 0], 180 - a));
    }
    const x = rootSet.has(o.id) ? 2 * cx - o.position[0] : -o.position[0];
    return { ...o, position: c3([x, o.position[1], o.position[2]]), rotationDeg: c3(quatToEulerDeg(rot)), params };
  });
}

/** Moves an object to another group (or the root), keeping its world transform. */
export function reparent(doc: TrackDocument, id: string, parent: string | undefined): TrackDocument {
  const o = findObject(doc, id);
  if (!o || o.parent === parent) return doc;
  if (parent !== undefined && (findObject(doc, parent)?.type !== 'group' || subtreeIds(doc, [id]).has(parent))) return doc;
  const world = worldTransformOf(doc, id);
  const moved: TrackDocument = { ...doc, objects: doc.objects.map((x) => (x.id === id ? { ...detached(o), ...(parent !== undefined ? { parent } : {}) } : x)) };
  return setWorldTransform(moved, id, world);
}

// ---------------------------------------------------------------- materials

export function addCustomMaterial(doc: TrackDocument, mat: CustomMaterial): TrackDocument {
  return { ...doc, materials: [...doc.materials.filter((m) => m.id !== mat.id), mat] };
}

/** Removes a custom material; objects using it fall back to `fallback`. */
export function deleteCustomMaterial(doc: TrackDocument, id: string, fallback = 'safe.stone'): TrackDocument {
  return {
    ...doc,
    materials: doc.materials.filter((m) => m.id !== id),
    objects: doc.objects.map((o) => (o.material === id ? { ...o, material: fallback } : o)),
  };
}

/** A free custom material id derived from a display name. */
export function customMaterialId(doc: TrackDocument, name: string): string {
  const stem = name.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'material';
  const used = new Set(doc.materials.map((m) => m.id));
  for (let n = 1; ; n++) {
    const id = n === 1 ? `custom.${stem}` : `custom.${stem}-${n}`;
    if (!used.has(id)) return id;
  }
}

export function inverseTransform(t: RigidTransform): RigidTransform {
  const r = conj(t.rotation);
  const p = rotateVec(r, t.position);
  return { position: [-p[0], -p[1], -p[2]], rotation: r };
}

/** Applies a world-space rigid motion (gizmo delta) to the top-level selected objects. */
export function applyWorldDelta(doc: TrackDocument, ids: Iterable<string>, delta: RigidTransform): TrackDocument {
  let out = doc;
  for (const id of topLevel(doc, ids)) out = setWorldTransform(out, id, compose(delta, worldTransformOf(doc, id)));
  return out;
}

/** `title` with `suffix` appended, shortened so the result stays within the format's title limit. */
export function titleWithSuffix(title: string, suffix: string): string {
  return `${title.slice(0, Math.max(0, LIMITS.maxNameLength - suffix.length)).trimEnd()}${suffix}`;
}
